import { useStoreContext } from '../context/StoreContext';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { track } from '../lib/analytics';
import { useCurrency } from '../context/CurrencyContext';
import { useTracking } from '../context/TrackingContext';
import CartUpsell from './CartUpsell';
import { ShoppingBag } from 'lucide-react';

const isVideoUrl = (url?: string): boolean => {
  if (!url) return false;
  const ext = url.split('.').pop()?.split('?')[0].toLowerCase() || '';
  return ['mp4', 'webm', 'ogg', 'mov', 'mkv'].includes(ext) || url.toLowerCase().includes('/video/');
};

export default function GlobalDrawers() {
  const { 
    cart, 
    cartTotal, 
    cartDrawerOpen, 
    closeCartDrawer, 
    openCartDrawer,
    removeFromCart, 
    updateQty, 
    linkTo,
    quickViewProduct,
    setQuickViewProduct,
    addToCart,
    subdomain
  } = useStoreContext();
  const { trackAddToCart } = useTracking();
  const { convert } = useCurrency();

  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('desc');

  useEffect(() => {
    if (cartDrawerOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        document.body.style.overflow = '';
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [cartDrawerOpen]);

  // Reset tab when product changes
  useEffect(() => {
    if (quickViewProduct) {
      setActiveTab('desc');
      // Track quick view as a product view
      track(subdomain, 'product_view', { productId: quickViewProduct.id });
    }
  }, [quickViewProduct, subdomain]);

  if (!cartDrawerOpen && !isVisible && !quickViewProduct) return null;

  function parseDescription(fullDesc: string = '') {
    let desc = fullDesc;
    let meta: any = {};
    if (fullDesc.includes('__metadata__:')) {
      const parts = fullDesc.split('__metadata__:');
      desc = parts[0].trim();
      try {
        meta = JSON.parse(parts[1]);
      } catch (e) {}
    }
    return { desc, meta };
  }

  const { desc, meta } = quickViewProduct ? parseDescription(quickViewProduct.description) : { desc: '', meta: {} };

  return (
    <>
      {/* ... (Cart Drawer remains unchanged) ... */}
      <div 
        onClick={closeCartDrawer}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 3000,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.3s ease',
          pointerEvents: cartDrawerOpen ? 'auto' : 'none'
        }}
      />
      
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        maxWidth: '420px',
        backgroundColor: 'var(--surface-card, #ffffff)',
        color: 'var(--text)',
        zIndex: 3001,
        boxShadow: '-10px 0 30px rgba(0,0,0,0.1)',
        transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{ padding: '24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 800 }}>Shopping Cart ({cart.length})</h2>
          <button onClick={closeCartDrawer} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text)' }}>✕</button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {cart.length === 0 ? (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <div style={{ marginBottom: '16px', color: 'var(--text-muted)' }}><ShoppingBag size={48} /></div>
              <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>Your cart is empty</p>
              <button 
                onClick={closeCartDrawer}
                style={{ background: 'var(--accent)', color: 'var(--accent-contrast, white)', padding: '12px 32px', borderRadius: 'var(--button-radius)', border: 'none', fontWeight: 700, cursor: 'pointer' }}
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {cart.map((item: any) => (
                <div key={`${item.id}-${item.name}`} style={{ display: 'flex', gap: '16px' }}>
                  {item.images && item.images[0] && isVideoUrl(item.images[0]) ? (
                    <div style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#000', flexShrink: 0 }}>
                      <video src={item.images[0]} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                      </div>
                    </div>
                  ) : (
                    <img src={item.images?.[0]} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border)' }} />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '4px' }}>{item.name}</div>
                    <div style={{ fontSize: '13px', color: 'var(--accent)', fontWeight: 700, marginBottom: '12px' }}>{convert(item.price)}</div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
                        <button onClick={() => updateQty(item.id, item.quantity - 1, item.name)} style={{ padding: '4px 10px', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text)' }}>-</button>
                        <span style={{ padding: '4px 8px', fontSize: '13px', fontWeight: 600 }}>{item.quantity}</span>
                        <button onClick={() => updateQty(item.id, item.quantity + 1, item.name)} style={{ padding: '4px 10px', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text)' }}>+</button>
                      </div>
                      <button onClick={() => removeFromCart(item.id, item.name)} style={{ fontSize: '12px', color: '#ff4d4f', background: 'none', border: 'none', cursor: 'pointer' }}>Remove</button>
                    </div>
                  </div>
                </div>
              ))}
              {/* Upsell section */}
              <CartUpsell />
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div style={{ padding: '24px', borderTop: '1px solid var(--border)', backgroundColor: 'var(--surface-card, #ffffff)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
              <span style={{ fontWeight: 600 }}>Subtotal</span>
              <span style={{ fontWeight: 800, fontSize: '18px' }}>{convert(cartTotal)}</span>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '20px' }}>Shipping and taxes calculated at checkout.</p>
            <Link 
              to={linkTo('/checkout')} 
              onClick={closeCartDrawer}
              style={{ 
                display: 'block', 
                backgroundColor: 'var(--accent)', 
                color: 'var(--accent-contrast, white)', 
                textAlign: 'center', 
                padding: '16px', 
                borderRadius: 'var(--button-radius)', 
                fontWeight: 700, 
                textDecoration: 'none' 
              }}
            >
              Checkout Now
            </Link>
          </div>
        )}
      </div>

      {/* --- QUICK VIEW MODAL --- */}
      {quickViewProduct && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(10px)',
          zIndex: 4000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          animation: 'fadeIn 0.3s ease'
        }} onClick={() => setQuickViewProduct(null)}>
          <div 
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--surface-card, #ffffff)',
              color: 'var(--text)',
              width: '100%',
              maxWidth: '1000px',
              maxHeight: 'min(90vh, 700px)',
              borderRadius: '24px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: window.innerWidth < 768 ? 'column' : 'row',
              position: 'relative',
              boxShadow: '0 30px 60px -12px rgba(0,0,0,0.3)',
              animation: 'modalSlideUp 0.4s cubic-bezier(0.2, 1, 0.3, 1)'
            }}
          >
            <button 
              onClick={() => setQuickViewProduct(null)}
              style={{ 
                position: 'absolute', 
                top: '20px', 
                right: '20px', 
                zIndex: 20, 
                background: 'var(--surface, rgba(255,255,255,0.9))', 
                border: '1px solid var(--border)', 
                width: '36px', 
                height: '36px', 
                borderRadius: '50%', 
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)', 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                transition: 'transform 0.2s',
                color: 'var(--text)'
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'rotate(90deg)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'rotate(0)'}
            >
              ✕
            </button>
            
            {/* Image Section */}
            <div style={{ 
              flex: '1.2', 
              backgroundColor: 'var(--surface, #F8F8F8)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              minHeight: window.innerWidth < 768 ? '300px' : 'auto'
            }}>
              {quickViewProduct.images && quickViewProduct.images[0] && isVideoUrl(quickViewProduct.images[0]) ? (
                <video
                  src={quickViewProduct.images[0]}
                  playsInline
                  autoPlay
                  loop
                  muted
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <img 
                  src={quickViewProduct.images?.[0]} 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'contain',
                    padding: '20px'
                  }} 
                />
              )}
            </div>
            
            {/* Info Section */}
            <div style={{ 
              flex: '1', 
              padding: window.innerWidth < 768 ? '24px' : '40px', 
              display: 'flex', 
              flexDirection: 'column',
              backgroundColor: 'var(--surface-card, white)',
              overflow: 'hidden' // Main container doesn't scroll, inner area does
            }}>
              <div style={{ marginBottom: '24px' }}>
                <h2 style={{ 
                  fontSize: 'clamp(20px, 2.5vw, 28px)', 
                  fontWeight: 900, 
                  lineHeight: '1.2', 
                  marginBottom: '12px',
                  fontFamily: 'var(--font-heading)',
                  letterSpacing: '-0.02em'
                }}>
                  {quickViewProduct.name}
                </h2>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px', fontWeight: 800, color: 'var(--accent)' }}>
                    {convert(quickViewProduct.price)}
                  </span>
                  {quickViewProduct.compare_price && quickViewProduct.compare_price > quickViewProduct.price && (
                    <span style={{ fontSize: '18px', textDecoration: 'line-through', color: 'var(--text-muted)', opacity: 0.6 }}>
                      {convert(quickViewProduct.compare_price)}
                    </span>
                  )}
                </div>
              </div>

              {/* TABS HEADER */}
              <div style={{ 
                display: 'flex', 
                gap: '24px', 
                borderBottom: '1px solid #EEEEEE', 
                marginBottom: '20px',
                overflowX: 'auto'
              }}>
                {[
                  { id: 'desc', label: 'Description' },
                  { id: 'features', label: 'Key Features' },
                  { id: 'specs', label: 'Specifications' },
                  { id: 'warranty', label: 'Warranty' }
                ].filter(t => t.id === 'desc' || meta?.[t.id]).map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      padding: '8px 0',
                      background: 'none',
                      border: 'none',
                      borderBottom: activeTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent',
                      fontSize: '13px',
                      fontWeight: activeTab === tab.id ? 700 : 500,
                      color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-muted)',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              
              {/* TABS CONTENT */}
              <div style={{ 
                fontSize: '14px', 
                lineHeight: '1.6', 
                color: 'var(--text-muted)', 
                marginBottom: '32px',
                overflowY: 'auto',
                flex: 1,
                paddingRight: '10px',
                whiteSpace: 'pre-line'
              }} className="custom-scrollbar">
                {activeTab === 'desc' && (desc || "Indulge in the premium quality and exceptional design of this exclusive item.")}
                {activeTab === 'features' && meta?.features}
                {activeTab === 'specs' && meta?.specs}
                {activeTab === 'warranty' && meta?.warranty}
              </div>
              
              {/* ACTIONS */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: 'auto' }}>
                <button 
                  onClick={() => { 
                    addToCart(quickViewProduct, 1); 
                    trackAddToCart(quickViewProduct, 1);
                    setQuickViewProduct(null); 
                    openCartDrawer(); 
                  }}
                  style={{ 
                    backgroundColor: 'var(--accent)', 
                    color: 'var(--accent-contrast, white)', 
                    padding: '16px', 
                    borderRadius: 'var(--button-radius)', 
                    border: 'none', 
                    fontWeight: 800, 
                    fontSize: '15px', 
                    cursor: 'pointer',
                    boxShadow: '0 8px 16px -4px var(--accent)',
                    transition: 'filter 0.2s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
                  onMouseLeave={e => e.currentTarget.style.filter = 'brightness(1)'}
                >
                  ADD TO CART
                </button>
                
                <Link 
                  to={linkTo(`/products/${quickViewProduct.id}`)} 
                  onClick={() => setQuickViewProduct(null)}
                  style={{ 
                    textAlign: 'center', 
                    padding: '10px',
                    fontSize: '13px', 
                    fontWeight: 600,
                    color: 'var(--text)', 
                    textDecoration: 'none',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--button-radius)'
                  }}
                >
                  VIEW FULL DETAILS
                </Link>
              </div>
            </div>
          </div>
          
          <style>{`
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes modalSlideUp { from { opacity: 0; transform: translateY(30px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
            .custom-scrollbar::-webkit-scrollbar { width: 4px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: var(--surface, #f1f1f1); }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: var(--border, #ccc); border-radius: 10px; }
          `}</style>
        </div>
      )}
    </>
  );
}
